import React from "react";
import { motion } from "framer-motion";
import CtaLink from "./CtaLink";
import "./FinalCtaPanel.css";

/**
 * The closing call to action shared by the Home, Services and About Us pages:
 * copy on the left, illustration on the right, on a deep gradient card.
 *
 * Content in, presentation only out — this component never supplies copy.
 *
 * @param {string} id        id for the heading, for the section's aria-labelledby
 * @param {string} title     the heading text
 * @param {string} paragraph supporting line beneath the heading
 * @param {object} cta       `{ to, label }` for the button
 * @param {object} [secondaryCta] optional second button; `{ href, label }` for
 *                 an in-page anchor, or `{ to, label }` for a route
 * @param {string} [image]   illustration shown beside the copy
 */
const FinalCtaPanel = ({ id, title, paragraph, cta, secondaryCta, image }) => (
  <section className="final-cta py-14 lg:py-20" aria-labelledby={id}>
    <div className="brand-col">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="final-cta__card px-6 py-9 sm:px-10 lg:px-12 lg:py-10"
      >
        <div className="final-cta__grid">
          <div className="text-center lg:text-left">
            <h2
              id={id}
              className="final-cta__title text-2xl md:text-3xl lg:text-4xl font-extrabold leading-tight tracking-tight text-white"
            >
              {title}
            </h2>
            <p className="mt-4 max-w-xl text-sm md:text-base leading-relaxed text-white/80">
              {paragraph}
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
              <CtaLink
                to={cta.to}
                label={cta.label}
                className="final-cta__btn"
              />
              {secondaryCta &&
                (secondaryCta.href ? (
                  /* an in-page anchor, so a plain <a> rather than CtaLink's
                     router Link — a Link would try to navigate to a route */
                  <a href={secondaryCta.href} className="final-cta__ghost">
                    {secondaryCta.label}
                  </a>
                ) : (
                  <CtaLink
                    to={secondaryCta.to}
                    label={secondaryCta.label}
                    variant="outline"
                  />
                ))}
            </div>
          </div>
          {image && (
            /* decorative: the heading beside it already carries the message */
            <div className="final-cta__art" aria-hidden="true">
              <img className="final-cta__img" src={image} alt="" loading="lazy" />
            </div>
          )}
        </div>
      </motion.div>
    </div>
  </section>
);

export default FinalCtaPanel;
